<!--
Toggle Component — bare ToggleSwitch wrapper without label/validation/layout.

Use when a domain template needs an inline toggle inside a list, table cell,
or compact UI where InputToggle's full-width label layout would not fit.

For form-style toggles with label + validation, use <input-toggle> instead.
-->

<template>
  <ToggleSwitch
    :id="elemId"
    v-model="internalValue"
    :disabled="isDisabled"
    :invalid="invalid"
    @change="onChange"
  />
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import ToggleSwitch from 'primevue/toggleswitch';

interface Props {
  elemId?: string;
  modelValue?: boolean;
  disabled?: boolean | string;
  invalid?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  elemId: undefined,
  modelValue: false,
  disabled: false,
  invalid: false
});

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  change: [event: Event];
}>();

const internalValue = ref<boolean>(props.modelValue ?? false);

const isDisabled = computed<boolean>(() => props.disabled === true || props.disabled === 'true');

const onChange = (event: Event): void => {
  if (isDisabled.value) return;
  emit('update:modelValue', internalValue.value);
  emit('change', event);
};

watch(() => props.modelValue, (next) => {
  internalValue.value = next ?? false;
});
</script>
